import React from 'react';
import { BarChart3 } from 'lucide-react';

/**
 * Poll Display Component
 * Shows poll question, options, voting functionality, and results
 */
const PollDisplay = ({ poll, currentUserId, onVote }) => {
  // Calculate vote counts
  const voteCounts = {};
  const totalVotes = poll.votes?.length || 0;
  const userVote = poll.votes?.find(v => v.user_id === currentUserId);

  poll.options?.forEach((_, index) => {
    voteCounts[index] = poll.votes?.filter(v => v.vote_option_index === index).length || 0;
  });

  // Check if poll has ended
  const hasEnded = new Date(poll.ends_at) < new Date();

  /**
   * Format time remaining
   */
  const getTimeRemaining = () => {
    const now = new Date();
    const end = new Date(poll.ends_at);
    const diff = end - now;

    if (diff <= 0) return 'Ended';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days} day${days > 1 ? 's' : ''} left`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} left`;
    return 'Ending soon';
  };

  return (
    <div className="max-w-lg bg-white/5 border border-white/10 rounded-lg p-4 my-2">
      {/* Poll Header */}
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 bg-blue-600/20 rounded-lg">
          <BarChart3 className="w-5 h-5 text-blue-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-white mb-1">{poll.question}</h3>
          <div className="flex items-center gap-3 text-xs text-gray-400">
            <span>{getTimeRemaining()}</span>
            <span>•</span>
            <span>{totalVotes} vote{totalVotes !== 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      {/* Poll Options */}
      <div className="space-y-2">
        {poll.options?.map((option, index) => {
          const voteCount = voteCounts[index] || 0;
          const percentage = totalVotes > 0 ? (voteCount / totalVotes) * 100 : 0;
          const isUserVote = userVote?.vote_option_index === index;

          return (
            <button
              key={index}
              onClick={() => !hasEnded && onVote(poll.id, index)}
              disabled={hasEnded}
              className={`w-full text-left relative overflow-hidden rounded-lg border transition-all ${
                hasEnded
                  ? 'cursor-not-allowed opacity-75'
                  : 'cursor-pointer hover:border-blue-500'
              } ${
                isUserVote
                  ? 'border-blue-500 bg-blue-500/10'
                  : 'border-white/10 bg-white/5'
              }`}
            >
              {/* Progress Bar */}
              <div
                className="absolute inset-0 bg-blue-500/20 transition-all duration-300"
                style={{ width: `${percentage}%` }}
              />

              {/* Option Content */}
              <div className="relative px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white font-medium">{option}</span>
                  {isUserVote && (
                    <span className="text-xs px-2 py-0.5 bg-blue-600 text-white rounded font-medium">
                      Your vote
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-300">{voteCount}</span>
                  <span className="text-sm font-semibold text-gray-200 min-w-[3rem] text-right">
                    {percentage.toFixed(0)}%
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Footer */}
      {!hasEnded && userVote && (
        <div className="mt-3 text-xs text-gray-400">
          Click another option to change your vote
        </div>
      )}
    </div>
  );
};

export default PollDisplay;
